import { ClientsService } from '@app/modules/admin/clients/clients.service'
import { OnDestroy, OnInit, ViewChild, Input, Component } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms'
import { Subject } from 'rxjs'
import { finalize, map, takeUntil, tap } from 'rxjs/operators'

import { takeFormSnapshot } from '@core/helpers/take-form-snapshot'
import { ConfirmUnsavedChanges } from '@core/guards/unsaved-changes.guard'
import { DocumentStatus, DocumentItem, DocumentRecord, DocumentStatusLabel, DocumentInput } from './document.model'
import { DocumentService } from './document.service'
import dayjs from 'dayjs'
import { LinValidators } from '@app/core/helpers/validators'
import { Asset } from '@app/shared/models/asset.model'
import { Driver, DriverTruncated } from '@app/shared/models/driver.model'
import { Vehicle, VehicleTruncated } from '@app/shared/models/vehicle.model'
import { PermissionSourceType, PermissionsSource } from '@app/core/types/permissions.source'
import { PermissionsService } from '@app/shared/services/permissions.service'
import { AssetsService } from '@app/modules/client/assets/services/assets.service'
import { VehiclesService } from '@app/modules/client/vehicles/vehicles.service'
import { RoleType } from '@app/core/types/role.type'

@Component({
  selector: 'lin-document-form',
  templateUrl: './document-form.component.html',
  styleUrls: ['./document-form.component.scss'],
  standalone: false,
})
export class DocumentFormComponent implements OnDestroy, OnInit, ConfirmUnsavedChanges {
  form: FormGroup
  isSubmitting = false
  isActualDirty = false
  isRecordAdded = false
  today = dayjs().tz()

  DocumentStatusLabel = DocumentStatusLabel

  @Input() document: DocumentItem
  @Input() documentSubject: 'driver' | 'vehicle' | 'asset'
  @Input() driver: Driver
  @Input() vehicle: Vehicle
  @Input() asset: Asset
  @Input() clientId: number

  private isFormChanged: ReturnType<typeof takeFormSnapshot>
  protected readonly onDestroy$ = new Subject<void>()

  get permissionSubject(): string {
    switch (this.documentSubject) {
      case 'vehicle':
        return 'FLEET'
      case 'driver':
        return 'DRIVERS'
      default:
        return 'ASSET'
    }
  }

  get isRecordMode(): boolean {
    const recordListPermission = `${this.permissionSubject}.DOCUMENTS_LIST.RECORDS_LIST`
    return this.hasPermission(<string>PermissionsSource[recordListPermission]) || !this.document
  }

  get isRecordAddMode(): boolean {
    const recordAddPermission = `${this.permissionSubject}.DOCUMENTS_LIST.RECORDS_LIST.ADD`
    return this.hasPermission(<string>PermissionsSource[recordAddPermission]) || !this.document
  }

  get isEditMode(): boolean {
    const editPermission = `${this.permissionSubject}.DOCUMENTS_LIST.EDIT`
    return (this.hasPermission(<string>PermissionsSource[editPermission]) && !this.isArchived) || !this.document
  }

  get isArchived(): boolean {
    return !!this.document && this.document.status === DocumentStatus.Archive
  }

  @ViewChild(FormGroupDirective, { static: true }) formGroupDirective: FormGroupDirective

  constructor(
    private readonly documentService: DocumentService,
    private readonly fb: FormBuilder,
    private readonly permissionsService: PermissionsService,
    private readonly clientsService: ClientsService,
    private readonly vehiclesService: VehiclesService,
    private readonly assetsService: AssetsService,
  ) {}

  ngOnInit() {
    this.initForm()
  }

  private initForm() {
    this.form = this.fb.group({
      title: ['', Validators.required],
      notifyBefore: [0, LinValidators.unsignedInt],
    })

    this.form.addControl(this.documentSubject, new FormControl(this[this.documentSubject]))
    this.form.addControl(`${this.documentSubject}Id`, new FormControl(this[this.documentSubject]?.id))

    this[this.documentSubject] && this.form.controls[this.documentSubject].disable()
    !this.isEditMode && this.form.disable()

    this.initFormValues()

    this.isFormChanged = takeFormSnapshot(this.form)
  }

  private initFormValues() {
    const { document } = this

    const subjectTarget = this[this.documentSubject] || (document && <Driver | Vehicle | Asset>document[this.documentSubject]) || null
    subjectTarget && this.form.get(this.documentSubject).patchValue(subjectTarget)
    subjectTarget && this.form.get(`${this.documentSubject}Id`).patchValue(subjectTarget.id)

    if (!this.document) return

    this.form.patchValue({
      ...(document.title && { title: document.title }),
      ...(document.notifyBefore && { notifyBefore: document.notifyBefore || 0 }),
    })

    this.isRecordAdded && this.form.get(this.documentSubject).disable()
    this.isArchived && this.form.disable()
  }

  getDriverList = (fullName: string) => {
    const toDriverTruncated = ({ id, fullName }: DriverTruncated) => ({ id, fullName })
    return this.clientsService
      .getClientUserList(this.clientId, { fullName, role: RoleType.Driver })
      .pipe(map((vehicles: DriverTruncated[]) => vehicles.map(toDriverTruncated)))
  }

  displayDriverName(driver?: DriverTruncated): string | null {
    return (driver && driver.fullName) || null
  }

  getVehicleList = (regNo: string) => {
    const toVehicleTruncated = ({ id, regNo }: Vehicle) => ({ id, regNo }) as VehicleTruncated
    return this.vehiclesService
      .getVehicleList(0, 30, null, { regNo })
      .pipe(map((vehicles: VehicleTruncated[]) => vehicles.map(toVehicleTruncated)))
  }

  displayVehicleRegNo(vehicle?: VehicleTruncated): string | null {
    return (vehicle && vehicle.regNo) || null
  }

  getAssetList = () => {
    const toAssetTruncated = ({ id, name }: Asset) => ({ id, name })
    return this.assetsService.getAssetList().pipe(map((asset: Asset[]) => asset.map(toAssetTruncated)))
  }

  displayAssetName(asset?: Partial<Asset>): string | null {
    return (asset && asset.name) || null
  }

  hasUnsavedChanges(): boolean {
    return this.isFormChanged()
  }

  private saveDocument() {
    this.isSubmitting = true
    const data = this.prepareDocumentInfoData()

    return this.documentService.edit(this.document.id, data).pipe(
      tap(() => (this.isFormChanged = takeFormSnapshot(this.form))),
      takeUntil(this.onDestroy$),
    )
  }

  submitForm() {
    this.saveDocument()
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe((document) => {
        this.document = document
        this.form.markAsUntouched()
        this.form.markAsPristine()
      })
  }

  editRecord({ record: editedRecord, index }: { record: DocumentRecord; index: number }): void {
    const record = this.formatRecordForSave(editedRecord)
    this.documentService
      .editRecord(this.document.id, record.id, record)
      .pipe(
        tap((record) => {
          const { issueDate, expDate, noExpiry } = record
          index === 0 && Object.assign(this.document, { ...this.document, issueDate, expDate, noExpiry })

          const records = [...this.document.records]
          records.splice(index, 1, record)
          this.document.records = records
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe()
  }

  onRecordAdded(document: DocumentItem): void {
    delete document.title
    delete document.notifyBefore

    this.document = document
    this.initFormValues()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private formatRecordForSave(data: any): DocumentRecord {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-argument
    data.issueDate = data.issueDate && dayjs(data.issueDate).tz().hour(0).minute(0).second(0).format()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-argument
    data.expDate = data.expDate && dayjs(data.expDate).tz().hour(0).minute(0).second(0).format()
    return data as DocumentRecord
  }

  private prepareDocumentInfoData(): DocumentInput {
    /* eslint-disable */
    const formData = this.form.value
    const shouldNotify = !!this.document?.expDate || (!this.document && !formData.noExpiry)

    let data = {
      title: formData.title,
      notifyBefore: shouldNotify ? formData.notifyBefore : 0,
    }

    data[`${this.documentSubject}Id`] = formData[`${this.documentSubject}Id`] || formData[this.documentSubject]?.id

    return data as unknown as DocumentInput
    /* eslint-enable */
  }

  private hasPermission(permission: PermissionSourceType): boolean {
    return this.permissionsService.hasPermissionSync(permission)
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
